import type {
	Constructor,
	IsEqual,
	OptionalKeysOf,
	RequiredKeysOf,
	Simplify,
} from "type-fest"

import type { StateValue } from "xstate"

/**
 * Generate integers from start (inclusive) to end (exclusive)
 * @param start
 * @param end
 */
export function range(start: number, end: number): Generator<number>
/**
 * Generate count integers starting from 0
 * @param count
 */
export function range(count: number): Generator<number>
export function* range(start: number, end?: number): Generator<number> {
	let actualStart = 0
	let actualEnd = start
	if (end !== undefined) {
		actualStart = start
		actualEnd = end
	}
	for (let i = actualStart; i < actualEnd; i++) {
		yield i
	}
}

/**
 * Tagged template literal for creating HTML templates from strings
 */
export function html(...args: Parameters<typeof String.raw>) {
	let html = String.raw(...args)
	let template = document.createElement("template")
	template.innerHTML = html
	return template
}

/** Options for {@link rotate2d} */
interface Rotate2dOptions {
	/** The x-coordinate of the center */
	cx?: number
	/** The y-coordinate of the center */
	cy?: number
	/** The x-coordinate of the point */
	x: number
	/** The y-coordinate of the point */
	y: number
	/** The angle to rotate the point by in radians */
	angle: number
}

/**
 * Rotate a point around a center in 2D space
 */
export function rotate2d({ cx = 0, cy = 0, x, y, angle }: Rotate2dOptions) {
	let cos = Math.cos(angle)
	let sin = Math.sin(angle)
	return {
		x: cos * (x - cx) + sin * (y - cy) + cx,
		y: cos * (y - cy) - sin * (x - cx) + cy,
	}
}

export type WithoutUndefined<T> = Simplify<
	{
		[K in keyof T]?: Exclude<T[K], undefined>
	} & {
		[K in keyof T as T[K] extends Exclude<T[K], undefined> ? K : never]: T[K]
	}
>

export function removeUndefinedProps<T extends object>(o: T) {
	let result = {} as WithoutUndefined<T>
	for (let [k, v] of Object.entries(o)) {
		if (v !== undefined) {
			result[k as keyof WithoutUndefined<T>] = v
		}
	}
	return result
}

export type Defaults<O extends object, D extends object> = Simplify<{
	[K in keyof (O & D)]: K extends RequiredKeysOf<WithoutUndefined<O>>
		? Required<WithoutUndefined<O>>[K]
		: K extends keyof O & OptionalKeysOf<D>
			? O[K] | D[K]
			: K extends keyof WithoutUndefined<O> & keyof D
				? Required<WithoutUndefined<O>>[K] | D[K]
				: K extends keyof D
					? D[K]
					: K extends keyof O
						? O[K]
						: never
}>

export function defaults<O extends object, D extends object>(
	o: O,
	defaults: D,
): Defaults<O, D> {
	let result = { ...o } as Defaults<O, D>
	for (let [k, v] of Object.entries(defaults)) {
		result[k as keyof D] =
			result[k as keyof D] === undefined ? v : result[k as keyof D]
	}
	return result
}

export type AssertExtends<T1, T2 extends T1, R = T2> = R
export type AssertEquals<T1, T2, R extends IsEqual<T1, T2>> = R

export type ObservedAttribute<Element extends Constructor<HTMLElement>> =
	Element extends {
		observedAttributes: readonly (infer A extends string)[]
	}
		? A
		: never

export function toFixed(value: number, precision: number) {
	let f = 10 ** precision
	return Math.round(value * f) / f
}

export function clamp(
	value: number,
	min?: number | null | undefined,
	max?: number | null | undefined,
) {
	if (min != null && max != null) {
		return Math.max(min, Math.min(max, value))
	}
	if (min != null) {
		return Math.max(min, value)
	}
	if (max != null) {
		return Math.min(max, value)
	}
	return value
}

type LongNotation<V extends StateValue, Sep extends string> = V extends string
	? V
	: keyof V extends string
		? {
				[K in keyof V]: V[K] extends StateValue
					? `${K}${Sep}${LongNotation<V[K], Sep>}`
					: V[K] extends undefined
						? K
						: never
			}[keyof V]
		: never

export function toLongNotation<V extends StateValue, S extends string>(
	value: V,
	separator: S,
): LongNotation<V, S>
export function toLongNotation<V extends StateValue>(
	value: V,
): LongNotation<V, ".">
export function toLongNotation(value: StateValue, separator = ".") {
	if (typeof value === "string") return value
	const k = Object.keys(value)[0] as string
	let sub = value[k]
	if (sub == null) return k
	let tail = toLongNotation(sub, separator)
	return `${k}${separator}${tail}`
}

/**
 * Creates a new object with only the specified keys from the original object.
 *
 * @param obj - The source object to pick properties from
 * @param keys - An array of keys to pick from the source object
 * @returns A new object containing only the picked properties
 *
 * @example
 * // Returns { name: 'John', age: 30 }
 * pick({ name: 'John', age: 30, city: 'New York' }, ['name', 'age'])
 */
export function pick<O extends object, K extends keyof O>(
	obj: O,
	keys: readonly K[],
): Pick<O, K> {
	let result: Partial<Pick<O, K>> = {}
	for (let k of keys) {
		result[k] = obj[k]
	}
	// @ts-expect-error - There is no way to assert that the object has all
	// the keys.
	return result
}

export function minDigits(num: number, digits: number) {
	return num.toString().padStart(digits, "0")
}
